import { useEffect, useState } from 'react';
import {
  CreditCard, AlertCircle, CheckCircle2, Save, Eye, EyeOff, Plus, FileText, Send,
  Users, TrendingUp, DollarSign, Receipt, RefreshCw, ShoppingCart, Package, Download, Trash2, X,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { billingApi, type StripeConfigResponse, type PortalStats, type BillingOrder } from '../../api/billing';
import { customerPortalApi, type PortalInvoice } from '../../api/customerPortal';
import { adminApi, type AdminUserSummary } from '../../api/admin';
import PricingPanel from './PricingPanel';
import { formatDate } from '../../utils/formatTime';

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const STATUS_TONE: Record<string, string> = {
  paid: 'bg-green-500/10 text-green-600 dark:text-green-400',
  open: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  draft: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  overdue: 'bg-red-500/10 text-red-600 dark:text-red-400',
  void: 'bg-slate-500/10 text-slate-500',
  pending_payment: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  canceled: 'bg-slate-500/10 text-slate-500',
};

function Metric({ icon: Icon, label, value, sub }: {
  icon: any; label: string; value: string; sub?: string;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-slate-500"><Icon size={16} /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
      <div className="mt-2 text-2xl font-bold text-slate-950 dark:text-slate-100">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
    </Card>
  );
}

export default function BillingApp({
  stripeConfig, onStripeSaved,
}: { stripeConfig: StripeConfigResponse | null; onStripeSaved: () => void }) {
  const [view, setView] = useState<'system' | 'pricing'>('system');
  const [form, setForm] = useState({ publishable_key: '', secret_key: '', webhook_secret: '', mode: 'test' });
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // System-wide analytics (owner-only).
  const [stats, setStats] = useState<PortalStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // System-wide orders (all users).
  const [orders, setOrders] = useState<Array<BillingOrder & { user_name?: string; user_email?: string | null }>>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // System invoices/notices (all users).
  const [invoices, setInvoices] = useState<PortalInvoice[]>([]);
  const [invLoading, setInvLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [invForm, setInvForm] = useState({
    user_id: '', customer_email: '', customer_name: '', product_label: '', amount: '',
    due_at: '', note: '', kind: 'invoice', send_email: true,
  });
  const [lineItems, setLineItems] = useState<Array<{ description: string; amount: string }>>([
    { description: '', amount: '' },
  ]);

  const stripeReady = Boolean(stripeConfig?.configured && stripeConfig?.is_active);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      mode: stripeConfig?.mode || 'test',
      publishable_key: stripeConfig?.publishable_key || '',
    }));
  }, [stripeConfig]);

  const loadStats = async () => {
    setStatsLoading(true);
    try { setStats(await billingApi.getPortalStats()); }
    catch { setStats(null); }
    finally { setStatsLoading(false); }
  };
  const loadOrders = async () => {
    setOrdersLoading(true);
    try { const d = await billingApi.getAllOrders(); setOrders(d.orders); }
    catch { setOrders([]); }
    finally { setOrdersLoading(false); }
  };
  const loadInvoices = async () => {
    setInvLoading(true);
    try { setInvoices(await customerPortalApi.listInvoices()); }
    catch { /* surfaced elsewhere */ }
    finally { setInvLoading(false); }
  };
  useEffect(() => { void loadStats(); void loadOrders(); void loadInvoices(); }, []);

  // Load users for the invoice picker (owner-only endpoint).
  useEffect(() => {
    adminApi.listUsers({ per_page: 100 }).then(d => setUsers(d.users)).catch(() => setUsers([]));
  }, []);

  const filteredUsers = users.filter(u => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  }).slice(0, 8);

  const selectedUser = users.find(u => u.id === invForm.user_id) || null;

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await billingApi.saveStripeConfig({
        publishable_key: form.publishable_key,
        secret_key: form.secret_key,
        webhook_secret: form.webhook_secret || undefined,
        mode: form.mode,
      });
      setForm(prev => ({ ...prev, secret_key: '', webhook_secret: '' }));
      setSuccess('Stripe configuration saved.');
      onStripeSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save Stripe config');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvoice = async () => {
    setCreating(true); setError(''); setSuccess('');
    try {
      // Build line items from the rows; the total is their sum (or the manual amount).
      const items = lineItems
        .filter(li => (li.description.trim() || li.amount.trim()))
        .map(li => ({
          description: li.description.trim() || 'Item',
          quantity: 1,
          amount_cents: Math.round(parseFloat(li.amount || '0') * 100) || 0,
        }));
      const itemTotal = items.reduce((s, it) => s + it.amount_cents, 0);
      const cents = items.length ? itemTotal : Math.round(parseFloat(invForm.amount || '0') * 100);

      if (!invForm.user_id) { setError('Attach the invoice to a user.'); setCreating(false); return; }
      if (isNaN(cents) || cents < 0) { setError('Enter a valid amount'); setCreating(false); return; }

      const res: any = await customerPortalApi.createInvoice({
        user_id: invForm.user_id,
        customer_email: invForm.customer_email || null,
        customer_name: invForm.customer_name || null,
        kind: invForm.kind,
        amount_cents: cents,
        line_items: items.length ? items : null,
        due_at: invForm.due_at ? new Date(invForm.due_at).toISOString() : null,
        note: invForm.note || null,
        send_email: invForm.send_email,
      });
      setInvForm({ user_id: '', customer_email: '', customer_name: '', product_label: '', amount: '', due_at: '', note: '', kind: 'invoice', send_email: true });
      setLineItems([{ description: '', amount: '' }]);
      setSuccess(res.email_sent ? 'Invoice created and emailed.' : 'Invoice created.');
      await loadInvoices();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const markPaid = async (id: string) => {
    try {
      await customerPortalApi.markInvoicePaid(id);
      await loadInvoices();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const syncCalendar = async () => {
    setSyncing(true); setError(''); setSuccess('');
    try {
      const res = await billingApi.syncBillingCalendar();
      setSuccess(`Calendar synced — ${res.created} new billing event${res.created === 1 ? '' : 's'} added.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Calendar sync failed');
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
        {([['system', 'System Billing'], ['pricing', 'Pricing & Margin']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setView(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${view === k ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500'}`}>
            {label}
          </button>
        ))}
      </div>

      {view === 'pricing' && <PricingPanel />}
      {view === 'system' && (
      <>
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
        System billing management — platform-wide revenue, subscriptions, orders and invoices for all users.
        Your own plan, cards and history live in <strong>Settings → Billing</strong>.
      </div>

      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      {/* System analytics */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">System Overview</h2>
          <Button size="sm" variant="secondary" onClick={() => loadStats()} disabled={statsLoading}>
            <RefreshCw size={14} />Refresh
          </Button>
          <Button size="sm" variant="secondary" onClick={syncCalendar} disabled={syncing}>
            <RefreshCw size={14} />{syncing ? 'Syncing…' : 'Sync to Calendar'}
          </Button>
        </div>
        {statsLoading ? (
          <div className="flex h-24 items-center justify-center"><LoadingSpinner size="md" /></div>
        ) : !stats ? (
          <p className="text-sm text-slate-500">Could not load system stats.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric icon={DollarSign} label="Revenue (paid)" value={money(stats.revenue_cents)} sub="all-time settled" />
            <Metric icon={TrendingUp} label="MRR" value={money(stats.mrr_cents)} sub="from active subscriptions" />
            <Metric icon={Users} label="Active subscriptions" value={String(stats.active_subscriptions)} sub={`${stats.total_subscriptions} total`} />
            <Metric icon={Users} label="Users" value={String(stats.total_users)} sub={`${stats.recent_signups.length} recent`} />
            <Metric icon={Receipt} label="Outstanding" value={money(stats.outstanding_cents)} sub={`${stats.open_invoices} open · ${stats.overdue_invoices} overdue`} />
            <Metric icon={AlertCircle} label="Failed payments" value={String(stats.failed_payments)} sub="non-settled events" />
            <Metric icon={TrendingUp} label="Churn (30d)" value={String(stats.churned_30d)} sub="cancellations" />
            <Metric icon={Package} label="Plan mix" value={Object.keys(stats.plan_counts).length ? Object.entries(stats.plan_counts).map(([k, v]) => `${k}:${v}`).join('  ') : '—'} sub="active subs by plan" />
            <Metric icon={DollarSign} label="Revenue by plan" value={Object.keys(stats.revenue_by_plan).length ? Object.entries(stats.revenue_by_plan).map(([k, v]) => `${k}:${money(v)}`).join('  ') : '—'} sub="settled per plan" />
          </div>
        )}
      </div>

      {/* System orders (all users) */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart size={18} className="text-slate-500" />
            <div>
              <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">System Orders</h2>
              <p className="text-sm text-slate-500">Add-on and plan orders across every user.</p>
            </div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => loadOrders()} disabled={ordersLoading}><RefreshCw size={14} />Refresh</Button>
        </div>
        {ordersLoading ? (
          <div className="flex h-20 items-center justify-center"><LoadingSpinner size="md" /></div>
        ) : orders.length === 0 ? (
          <p className="text-sm text-slate-500">No orders yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500 dark:border-slate-700">
                  <th className="pb-2 pr-4 font-medium">User</th>
                  <th className="hidden pb-2 pr-4 font-medium sm:table-cell">Plan</th>
                  <th className="hidden pb-2 pr-4 font-medium sm:table-cell">Items</th>
                  <th className="pb-2 pr-4 font-medium">Total</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="hidden pb-2 font-medium sm:table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
                    <td className="py-2 pr-4 text-slate-700 dark:text-slate-300">{o.user_name || o.user_email || '—'}</td>
                    <td className="hidden py-2 pr-4 text-slate-500 sm:table-cell">{o.plan_key || '—'}</td>
                    <td className="hidden py-2 pr-4 text-slate-500 sm:table-cell">{o.items?.length ?? 0}</td>
                    <td className="py-2 pr-4 text-slate-950 dark:text-slate-100">{money(o.total_cents)}</td>
                    <td className="py-2 pr-4">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[o.status] || 'bg-slate-500/10 text-slate-500'}`}>{o.status}</span>
                    </td>
                    <td className="hidden py-2 text-slate-500 sm:table-cell">{o.created_at ? formatDate(o.created_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Payment processor */}
      <Card className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <CreditCard size={18} className="text-slate-500" />
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Payment Processor</h2>
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${stripeReady ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>
            {stripeReady ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          </div>
          <div>
            <div className="text-sm font-medium text-slate-950 dark:text-slate-100">{stripeReady ? 'Stripe Active' : 'Stripe Not Configured'}</div>
            <div className="text-xs text-slate-500">{stripeConfig?.configured ? `Mode: ${stripeConfig.mode}` : 'Enter your API keys below to enable billing.'}</div>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">Mode</label>
          <select value={form.mode} onChange={e => setForm(p => ({ ...p, mode: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
            <option value="test">Test Mode</option>
            <option value="live">Live Mode</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">Publishable Key</label>
          <input type="text" value={form.publishable_key} onChange={e => setForm(p => ({ ...p, publishable_key: e.target.value }))}
            placeholder="pk_live_... or pk_test_..." className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
            Secret Key {stripeConfig?.has_secret_key && <span className="text-xs text-slate-400">(leave blank to keep existing)</span>}
          </label>
          <div className="relative">
            <input type={showSecret ? 'text' : 'password'} value={form.secret_key} onChange={e => setForm(p => ({ ...p, secret_key: e.target.value }))}
              placeholder={stripeConfig?.has_secret_key ? '•••••••• (stored)' : 'sk_...'}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-10 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
            <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><Eye size={16} /></button>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
            Webhook Secret {stripeConfig?.has_webhook_secret && <span className="text-xs text-slate-400">(leave blank to keep existing)</span>}
          </label>
          <div className="relative">
            <input type={showWebhook ? 'text' : 'password'} value={form.webhook_secret} onChange={e => setForm(p => ({ ...p, webhook_secret: e.target.value }))}
              placeholder={stripeConfig?.has_webhook_secret ? '•••••••• (stored)' : 'whsec_...'}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-10 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
            <button type="button" onClick={() => setShowWebhook(!showWebhook)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><EyeOff size={16} /></button>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            Webhook endpoint: <code className="break-all rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">{window.location.origin}/api/v1/billing/webhook</code>
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving || !form.publishable_key}>
          {saving ? <LoadingSpinner size="sm" /> : <Save size={16} />}{saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Card>

      {/* Invoices & notices */}
      <Card className="p-5">
        <div className="mb-4 flex items-center gap-2">
          <FileText size={18} className="text-slate-500" />
          <div>
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Invoices &amp; Payment Notices</h2>
            <p className="text-sm text-slate-500">Issue invoices or payment notices to customers and track settlement.</p>
          </div>
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* Attach to a user — required (no orphan invoices) */}
          <div className="sm:col-span-2 lg:col-span-1">
            <input placeholder="Search user by name or email…" value={userQuery}
              onChange={e => setUserQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
            {selectedUser ? (
              <div className="mt-1 flex items-center justify-between rounded-lg bg-brand-500/10 px-2 py-1.5 text-xs text-brand-700 dark:text-brand-300">
                <span className="truncate">Attached: <strong>{selectedUser.name || selectedUser.email}</strong></span>
                <button onClick={() => setInvForm(p => ({ ...p, user_id: '' }))} className="ml-2 text-brand-600"><X size={12} /></button>
              </div>
            ) : (
              userQuery && (
                <div className="mt-1 max-h-40 overflow-auto rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  {filteredUsers.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No users match.</div>}
                  {filteredUsers.map(u => (
                    <button key={u.id} onClick={() => { setInvForm(p => ({ ...p, user_id: u.id, customer_name: u.name || '', customer_email: u.email })); setUserQuery(''); }}
                      className="block w-full px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                      {u.name || u.email} <span className="text-slate-400">{u.email}</span>
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
          <select value={invForm.kind} onChange={e => setInvForm(p => ({ ...p, kind: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
            <option value="invoice">Invoice</option>
            <option value="notice">Payment notice</option>
          </select>
          <input type="date" value={invForm.due_at}
            onChange={e => setInvForm(p => ({ ...p, due_at: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
        </div>

        {/* Line items: product/add-on OR custom. Falls back to a single amount below. */}
        <div className="mb-4 space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Line items (or leave blank for a single amount)</div>
          {lineItems.map((li, idx) => (
            <div key={idx} className="flex flex-wrap gap-2">
              <input placeholder="Description (product, add-on, or custom)" value={li.description}
                onChange={e => setLineItems(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                className="min-w-[10rem] flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
              <input placeholder="Amount" type="number" step="0.01" value={li.amount}
                onChange={e => setLineItems(prev => prev.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                className="w-28 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
              <button onClick={() => setLineItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)}
                className="rounded-lg border border-slate-300 p-2 text-slate-400 hover:text-red-500 dark:border-slate-600" aria-label="Remove line"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setLineItems(prev => [...prev, { description: '', amount: '' }])}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-500"><Plus size={12} />Add line</button>
        </div>

        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <input placeholder="Amount (USD) — used only if no line items" type="number" step="0.01" value={invForm.amount}
            onChange={e => setInvForm(p => ({ ...p, amount: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <input placeholder="Note (optional)" value={invForm.note}
            onChange={e => setInvForm(p => ({ ...p, note: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={invForm.send_email} onChange={e => setInvForm(p => ({ ...p, send_email: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" />
            Email it to the user on create
          </label>
        </div>
        <Button onClick={handleCreateInvoice} disabled={creating || !invForm.user_id} className="mb-5">
          {creating ? <LoadingSpinner size="sm" /> : <Plus size={16} />}Create invoice
        </Button>

        {invLoading ? (
          <div className="flex h-24 items-center justify-center"><LoadingSpinner size="md" /></div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-slate-500">No invoices or notices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500 dark:border-slate-700">
                  <th className="pb-2 pr-4 font-medium">Number</th>
                  <th className="pb-2 pr-4 font-medium">Customer</th>
                  <th className="hidden pb-2 pr-4 font-medium sm:table-cell">Kind</th>
                  <th className="pb-2 pr-4 text-right font-medium">Amount</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="hidden pb-2 pr-4 font-medium sm:table-cell">Due</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
                    <td className="py-2 pr-4 font-mono text-xs whitespace-nowrap text-slate-700 dark:text-slate-300">{inv.number}</td>
                    <td className="py-2 pr-4 text-slate-700 dark:text-slate-300">{inv.customer_email || inv.customer_name || '—'}</td>
                    <td className="hidden py-2 pr-4 capitalize text-slate-500 sm:table-cell">{inv.kind}</td>
                    <td className="py-2 pr-4 text-right text-slate-950 dark:text-slate-100">{money(inv.amount_cents)}</td>
                    <td className="py-2 pr-4">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[inv.status] || 'bg-slate-500/10 text-slate-500'}`}>{inv.status}</span>
                    </td>
                    <td className="hidden py-2 pr-4 whitespace-nowrap text-slate-500 sm:table-cell">{inv.due_at ? formatDate(inv.due_at) : '—'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button onClick={() => customerPortalApi.downloadInvoicePdf(inv.id, inv.number)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-500" title="Download PDF">
                        <Download size={12} />PDF
                      </button>
                      {inv.status !== 'paid' && (
                        <button onClick={() => markPaid(inv.id)} className="ml-3 inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-500">
                          <Send size={12} />Mark paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      </>
      )}
    </div>
  );
}
